/**
 * Freezes the billing fee metadata (`handlingFee`, `transportFee`,
 * `transportVatRate`, `beforeVat`, `vat`) on each cart line's `sale` object at
 * order creation time (checkout), instead of recalculating it at billing time
 * from the current seller/program configuration.
 *
 * Replicates the fee rules of the admin billing `prepareData`:
 *  - `handlingFee` from the seller (or its `programOverrides[nick]`), charged
 *    once per product/emission group when the program's
 *    `handlingFeePerProductFromDate` cutoff is active, per line before it
 *  - `transportFee` from the seller (or override); for emission lines it uses
 *    the seller's `transportFeeRanges` tiers based on the emission count of
 *    that product in the order; charged once per product/emission group when
 *    the cutoff is active, per line before it
 *  - all transport fees are 0 when the order already has an `isTransport`
 *    line (user already paid shipping at checkout)
 *  - non-emission lines end with transportFee 0
 *  - `beforeVat = amount / (1 + vatRate/100)` and `vat = amount - beforeVat`,
 *    both rounded to 2 decimals
 *
 * Mutates each item's `sale` in place (creates it when missing).
 */

export interface SaleFeesOptions {
  nick: string
  handlingFeePerProductFromDate?: string
  segment?: any
  programVatRate?: number
}

export function applySaleFees(cartItems: any[], sellers: Record<string, any>, opts: SaleFeesOptions): void {
  if (!Array.isArray(cartItems) || !cartItems.length) return

  const handlingFeeCutoffDate = opts.handlingFeePerProductFromDate ? new Date(opts.handlingFeePerProductFromDate) : null
  const orderHasTransport = cartItems.some((c: any) => c.isTransport)

  // Emission line count per product, for tiered transport fees
  // (orders without an isTransport line only, same as billing)
  const emissionCounts: Record<string, number> = {}
  if (!orderHasTransport) {
    for (const item of cartItems) {
      if (!item.name || item.name.indexOf('emissão') < 0) continue
      emissionCounts[item.id] = (emissionCounts[item.id] || 0) + 1
    }
  }

  const seenHandlingGroups = new Set<string>()
  const seenTransportGroups = new Set<string>()

  for (const item of cartItems) {
    if (!item.sale) {
      item.sale = {
        qty: +item.qty || 0,
        price: +item.price || 0,
        vatRate: +item.vatRate || 0,
        amount: (+item.qty || 0) * (+item.price || 0),
        orderDate: item.orderDate ? new Date(item.orderDate) : null
      }
    }
    const sale = item.sale
    const seller = item.seller || item.serviceName || ''
    const sellerObj = sellers[seller]
    const override = sellerObj?.programOverrides?.[opts.nick]
    const isEmission = !!item.name && item.name.indexOf('emissão') >= 0

    const baseHandlingFee = sellerObj && sale.qty ? Number(override?.handlingFee ?? sellerObj.handlingFee) || 0 : 0
    if (handlingFeeCutoffDate) {
      const groupKey = `${item.id}|${isEmission}`
      if (seenHandlingGroups.has(groupKey)) {
        sale.handlingFee = 0
      } else {
        seenHandlingGroups.add(groupKey)
        sale.handlingFee = baseHandlingFee
      }
    } else {
      sale.handlingFee = baseHandlingFee
    }

    const baseTransportationFee = sellerObj ? Number(override?.transportationFee ?? sellerObj.transportationFee) || 0 : 0
    if (orderHasTransport) {
      sale.transportFee = 0
    } else if (handlingFeeCutoffDate) {
      const groupKey = `${item.id}|${isEmission}`
      const emissionCount = emissionCounts[item.id]
      if (isEmission && emissionCount) {
        if (seenTransportGroups.has(groupKey)) {
          sale.transportFee = 0
        } else {
          seenTransportGroups.add(groupKey)
          const ranges = sellerObj?.transportFeeRanges
          const sortedRanges = ranges ? [...ranges].sort((a: any, b: any) => a.max - b.max) : []
          const tier = sortedRanges.find((r: any) => emissionCount <= r.max)
          sale.transportFee = tier ? Number(tier.cost) : baseTransportationFee
        }
      } else if (seenTransportGroups.has(groupKey)) {
        sale.transportFee = 0
      } else {
        seenTransportGroups.add(groupKey)
        sale.transportFee = baseTransportationFee
      }
    } else {
      sale.transportFee = baseTransportationFee
    }

    sale.transportVatRate = sellerObj ? Number(override?.vatTransportationFee ?? sellerObj.vatTransportationFee) || 0 : 0

    // Non-emission lines carry no transport fees, same as billing
    if (!isEmission) {
      sale.transportFee = 0
      sale.transportVatRate = 0
    }

    // VAT rate: when the segment configures rates, the product's own rate
    // decides which one applies: a product at the program's normal rate gets
    // segment.vatRate, a reduced-rate product gets segment.lowVatRate. The
    // product's own rate is the fallback when the segment has none (or the
    // matching field is not set). An explicit 0 is a valid (exempt) rate, so
    // the check is field presence, not truthiness.
    const segVat = opts.segment?.vatRate
    const segLowVat = opts.segment?.lowVatRate
    if (segVat != null || segLowVat != null) {
      const productRate = +item.vatRate || 0
      const programRate = opts.programVatRate ?? 23
      if (productRate === programRate && segVat != null) sale.vatRate = Number(segVat) || 0
      else if (productRate !== programRate && segLowVat != null) sale.vatRate = Number(segLowVat) || 0
    }

    sale.beforeVat = Math.round((sale.amount || 0) * 100 / (1 + (sale.vatRate || 0) / 100)) / 100
    sale.vat = Math.round(((sale.amount || 0) - sale.beforeVat) * 100) / 100
  }
}
